import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Badge, Alert, Spinner } from 'react-bootstrap';
import { Users, Target, Zap, Mail, Phone } from 'lucide-react';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';

interface ClientMatcherProps {}

interface MatchCriteria {
  priceRange: { min: number; max: number };
  propertyTypes: string[];
  locations: string[];
  minScore: number;
}

interface ClientProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  preferences: {
    budget: { min: number; max: number };
    propertyTypes: string[];
    locations: string[];
  };
  lastContact?: string;
  status: string;
}

interface ClientMatch {
  client: ClientProfile;
  score: number;
  properties: any[];
  reasons: string[];
}

const ClientMatcher: React.FC<ClientMatcherProps> = () => {
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [matchCriteria, setMatchCriteria] = useState<MatchCriteria>({
    priceRange: { min: 0, max: 1000000 },
    propertyTypes: [],
    locations: [],
    minScore: 80
  });

  const [clients, setClients] = useState<any>(null);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [matchResults, setMatchResults] = useState<ClientMatch[]>([]);
  const [matchingLoading, setMatchingLoading] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setClientsLoading(true);
        const response = await axiosInstance.get('/api/mls-portal/client-profiles');
        if (response.data.success) {
          setClients(response.data.data);
        } else {
          throw new Error(response.data.message || 'Failed to fetch clients');
        }
      } catch (error: any) {
        console.error('Error fetching client profiles:', error);
        // Set mock data for development
        setClients({
          total: 45,
          active: 32,
          matched: 18,
          converted: 6,
          clients: []
        });
      } finally {
        setClientsLoading(false);
      }
    };

    fetchClients();
  }, []);

  const handleRunMatching = async () => {
    try {
      setMatchingLoading(true);
      const response = await axiosInstance.post('/api/mls-portal/match-clients', matchCriteria);
      
      if (response.data.success) {
        setMatchResults(response.data.data.matches || []);
        toast.success(`Found ${response.data.data.matches?.length || 0} client matches!`);
      } else {
        throw new Error(response.data.message || 'Failed to match clients');
      }
    } catch (error: any) {
      console.error('Error running client matching:', error);
      
      // Set mock results for development
      const mockMatches: ClientMatch[] = [
        {
          client: {
            id: '1',
            name: 'Sarah Johnson',
            email: 'sarah@example.com',
            phone: '(555) 123-4567',
            preferences: {
              budget: { min: 400000, max: 550000 },
              propertyTypes: ['Single Family'],
              locations: ['Phoenix', 'Tempe']
            },
            lastContact: '2 days ago',
            status: 'active'
          },
          score: 92,
          properties: [
            { id: 'p1', address: '123 Main St', price: 485000 },
            { id: 'p2', address: '456 Oak Ave', price: 520000 }
          ],
          reasons: ['Price Range Match', 'Location Preference', 'Property Type']
        },
        {
          client: {
            id: '2',
            name: 'Mike Chen',
            email: 'mike@example.com',
            phone: '(555) 987-6543',
            preferences: {
              budget: { min: 300000, max: 450000 },
              propertyTypes: ['Condo', 'Townhouse'],
              locations: ['Scottsdale', 'Phoenix']
            },
            lastContact: '1 week ago',
            status: 'interested'
          },
          score: 87,
          properties: [
            { id: 'p3', address: '789 Pine Rd', price: 395000 }
          ],
          reasons: ['Budget Match', 'Amenities', 'School District']
        }
      ];
      
      setMatchResults(mockMatches);
      toast.success(`Found ${mockMatches.length} client matches!`);
    } finally {
      setMatchingLoading(false);
    }
  };

  const handleContactClient = (client: ClientProfile, method: 'email' | 'phone') => {
    if (method === 'email') {
      window.location.href = `mailto:${client.email}`;
    } else if (method === 'phone' && client.phone) {
      window.location.href = `tel:${client.phone}`;
    }
  };

  return (
    <div className="client-matcher">
      <Row className="g-4">
        {/* Matching Criteria */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="border-bottom-0 pb-0">
              <h6 className="mb-0">
                <Target size={18} className="me-2" />
                Matching Criteria
              </h6>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Price Range</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="number"
                      placeholder="Min"
                      value={matchCriteria.priceRange.min}
                      onChange={(e) => setMatchCriteria({
                        ...matchCriteria,
                        priceRange: {
                          ...matchCriteria.priceRange,
                          min: parseInt(e.target.value) || 0
                        }
                      })}
                    />
                    <Form.Control
                      type="number"
                      placeholder="Max"
                      value={matchCriteria.priceRange.max}
                      onChange={(e) => setMatchCriteria({
                        ...matchCriteria,
                        priceRange: {
                          ...matchCriteria.priceRange,
                          max: parseInt(e.target.value) || 1000000
                        }
                      })}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Property Types</Form.Label>
                  {['Single Family', 'Condo', 'Townhouse', 'Multi-Family'].map(type => (
                    <Form.Check
                      key={type}
                      type="checkbox"
                      id={`type-${type}`}
                      label={type}
                      checked={matchCriteria.propertyTypes.includes(type)}
                      onChange={(e) => {
                        const updatedTypes = e.target.checked
                          ? [...matchCriteria.propertyTypes, type]
                          : matchCriteria.propertyTypes.filter(t => t !== type);
                        setMatchCriteria({
                          ...matchCriteria,
                          propertyTypes: updatedTypes
                        });
                      }}
                    />
                  ))}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Minimum Match Score</Form.Label>
                  <Form.Range
                    min={60}
                    max={100}
                    value={matchCriteria.minScore}
                    onChange={(e) => setMatchCriteria({
                      ...matchCriteria,
                      minScore: parseInt(e.target.value)
                    })}
                  />
                  <div className="text-center">
                    <small className="text-muted">{matchCriteria.minScore}% or higher</small>
                  </div>
                </Form.Group>

                <div className="d-grid">
                  <Button 
                    variant="primary" 
                    onClick={handleRunMatching}
                    disabled={matchingLoading}
                  >
                    {matchingLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Matching...
                      </>
                    ) : (
                      <>
                        <Zap size={16} className="me-2" />
                        Run AI Matching
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Client Stats */}
          <Card className="border-0 shadow-sm mt-3">
            <Card.Header className="border-bottom-0 pb-0">
              <h6 className="mb-0">Client Database</h6>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <h4 className="mb-1">{clients?.total || 0}</h4>
                <p className="text-muted mb-3">Total Clients</p>
                
                <div className="d-flex justify-content-around text-center">
                  <div>
                    <h6 className="mb-1">{clients?.active || 0}</h6>
                    <small className="text-success">Active</small>
                  </div>
                  <div>
                    <h6 className="mb-1">{clients?.matched || 0}</h6>
                    <small className="text-info">Matched</small>
                  </div>
                  <div>
                    <h6 className="mb-1">{clients?.converted || 0}</h6>
                    <small className="text-warning">Converted</small>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Match Results */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="border-bottom-0 pb-0">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <Users size={18} className="me-2" />
                  Client Matches
                </h6>
                {matchResults.length > 0 && (
                  <Badge bg="success" className="rounded-pill">
                    {matchResults.length} matches found
                  </Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {matchingLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Finding perfect matches...</p>
                </div>
              ) : matchResults.length > 0 ? (
                <div className="matches-list">
                  {matchResults.map((match: ClientMatch, index: number) => (
                    <div key={index} className="match-item p-3 border rounded mb-3">
                      <Row className="align-items-center">
                        <Col md={8}>
                          <div className="d-flex align-items-start">
                            <div className="avatar-lg bg-soft-primary rounded-circle me-3 d-flex align-items-center justify-content-center">
                              <Users className="text-primary" />
                            </div>
                            <div>
                              <h6 className="mb-1">{match.client.name}</h6>
                              <p className="text-muted mb-2">{match.client.email}</p>
                              <div className="mb-2">
                                <Badge bg="success" className="me-2">
                                  {match.score}% Match
                                </Badge>
                                <small className="text-muted">
                                  {match.properties.length} matching properties
                                </small>
                              </div>
                              <div className="match-reasons">
                                {match.reasons.slice(0, 3).map((reason: string, i: number) => (
                                  <Badge key={i} bg="light" text="dark" className="me-1 mb-1">
                                    {reason}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4} className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => handleContactClient(match.client, 'email')}
                            >
                              <Mail size={14} className="me-1" />
                              Email
                            </Button>
                            {match.client.phone && (
                              <Button 
                                variant="outline-success" 
                                size="sm"
                                onClick={() => handleContactClient(match.client, 'phone')}
                              >
                                <Phone size={14} className="me-1" />
                                Call
                              </Button>
                            )}
                          </div>
                          <small className="text-muted d-block mt-2">
                            Last contact: {match.client.lastContact || 'Never'}
                          </small>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <Target size={48} className="text-muted mb-3" />
                  <h6 className="text-muted">Ready to Find Perfect Matches</h6>
                  <p className="text-muted">
                    Set your criteria and run AI matching to find clients 
                    interested in available properties.
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ClientMatcher;